import { apiClient } from '@/core/api/client';

/**
 * Backend referral-validation endpoints used to attribute pre-login visitors
 * to the FP / SP / partner that drove them to the platform.
 *
 * Both endpoints are public (no auth) and already implemented:
 * - GET /referral/:refCode/validate → FP or SP individual referral
 * - GET /partner-referral/:code     → Partner company referral
 */

export type FpSpReferralResponse = {
  referralCode: string;
  professionalId: string;
  professionalName: string;
  companyName?: string;
  role: 'SERVICE_PROVIDER' | 'FINANCIAL_ADVISOR';
};

export type PartnerReferralResponse = {
  id: string;
  name: string;
  referralCode: string;
  logo: string | null;
};

/** Validate an individual FP/SP referral code. Returns null on 404 (invalid code). */
export const validateFpSpReferral = async (
  refCode: string,
): Promise<FpSpReferralResponse | null> => {
  try {
    const response = await apiClient.get<FpSpReferralResponse>(
      `/referral/${encodeURIComponent(refCode)}/validate`,
    );
    return response.data;
  } catch (err: any) {
    if (err?.statusCode === 404 || err?.statusCode === 400) return null;
    throw err;
  }
};

/** Validate a partner-company referral code. Returns null on 404 (invalid code). */
export const validatePartnerReferral = async (
  code: string,
): Promise<PartnerReferralResponse | null> => {
  try {
    const response = await apiClient.get<PartnerReferralResponse>(
      `/partner-referral/${encodeURIComponent(code)}`,
    );
    return response.data;
  } catch (err: any) {
    if (err?.statusCode === 404 || err?.statusCode === 400) return null;
    throw err;
  }
};
